import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { ChevronDown } from "lucide-react";

const customStyles = (
  error,
  controlHeight,
  mutedValue,
  wrapMultiValues,
  isMulti,
) => {
  const shouldWrap = Boolean(wrapMultiValues || isMulti);

  return {
    control: (provided, state) => ({
      ...provided,

      backgroundColor: state.isDisabled
        ? "var(--admin-field-disabled, #f3f4f6)"
        : "var(--admin-field)",

      borderColor: error
        ? "var(--admin-danger)"
        : state.isDisabled
          ? "var(--admin-field-line)"
          : state.isFocused
            ? ""
            : "var(--admin-field-line)",

      boxShadow: error ? "none" : state.isFocused ? "" : "none",

      borderRadius: "0.375rem",

      // Keep height consistent for single select, but allow auto expansion when wrapping multi values
      minHeight: controlHeight ? `${controlHeight}px` : "36px",
      height: shouldWrap
        ? "auto"
        : controlHeight
          ? `${controlHeight}px`
          : "36px",
      maxHeight: shouldWrap
        ? "none"
        : controlHeight
          ? `${controlHeight}px`
          : "36px",

      boxSizing: "border-box",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.7 : 1,

      "&:hover": {
        borderColor: error
          ? "var(--admin-danger)"
          : state.isDisabled
            ? "var(--admin-field-line)"
            : "",
      },
    }),

 valueContainer: (provided) => ({
  ...provided,
  padding: shouldWrap ? "3px 6px" : "2px 8px",
  gap: shouldWrap ? "4px" : undefined,
  display: "flex",
  alignItems: "center",
  flexWrap: shouldWrap ? "wrap" : "nowrap",
  minWidth: 0,
}),
   placeholder: (provided, state) => ({
  ...provided,
  color: state.isDisabled ? "#9ca3af" : "var(--admin-muted)",
  fontSize: "0.875rem",
  paddingLeft: "2px",
  position: "absolute",
  pointerEvents: "none",
}),

    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled
        ? "#9ca3af"
        : mutedValue
          ? "var(--admin-muted)"
          : "var(--admin-ink)",
      fontSize: "0.875rem",
      paddingLeft: "2px",
    }),

input: (provided) => ({
  ...provided,
  margin: 0,
  padding: 0,
  textAlign: "left",
}),

    multiValue: (provided) => ({
      ...provided,
      minWidth: 0,
      maxWidth: "100%",
      margin: "2px",
      borderRadius: "4px",
    }),

    multiValueLabel: (provided) => ({
      ...provided,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: "12px",
    }),

    multiValueRemove: (provided) => ({
      ...provided,
      cursor: "pointer",
      ":hover": {
        backgroundColor: "rgba(239, 68, 68, 0.15)",
        color: "var(--admin-danger, #ef4444)",
      },
    }),

    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: "0 8px 0 4px",
      color: state.isDisabled ? "#9ca3af" : provided.color,
    }),

    indicatorsContainer: (provided) => ({
      ...provided,
      flexShrink: 0,
      alignSelf: shouldWrap ? "flex-start" : "center",
      paddingTop: shouldWrap ? "4px" : 0,
    }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    zIndex: 100200,
    position: "absolute",
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "var(--admin-navy)"
      : state.isFocused
        ? "var(--admin-blue-soft)"
        : provided.backgroundColor,
    color: state.isSelected ? "#fff" : "var(--admin-ink)",
    fontSize: "13px",
    padding: "8px 12px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  }),

  menuList: (provided) => ({
    ...provided,
    scrollbarColor: "#1F1B5F transparent",
    scrollbarWidth: "thin",

    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },

    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },

    "&::-webkit-scrollbar-thumb": {
      background: "#1F1B5F",
      borderRadius: "4px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      background: "#16134a",
    },
  }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 100200,
    }),
  };
};

/**
 * Custom dropdown arrow
 * Rotates when the react-select menu is open.
 */
const CustomDropdownIndicator = (props) => {
  const { selectProps } = props;
  const isOpen = selectProps.menuIsOpen;

  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown
        size={18}
        strokeWidth={2}
        className={`transition-transform duration-200 ${
          isOpen ? "rotate-180" : "rotate-0"
        }`}
      />
    </components.DropdownIndicator>
  );
};

export const ThreeDotLoadingIndicator = () => (
  <span
    className="inline-flex items-center gap-0.5 text-gray-400"
    role="status"
    aria-label="Loading"
  >
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
  </span>
);

const CustomLoadingIndicator = (props) => (
  <components.LoadingIndicator {...props}>
    <ThreeDotLoadingIndicator />
  </components.LoadingIndicator>
);

const FilterSelect = ({
  label,
  options,
  value,
  onChange,
  isDisabled = false,
  placeholder = label ? label : "Search by User's Name or Username",
  isMulti = false,
  isCreatable = false,
  error = "",
  required,
  name,
  inputId = name,
  isLoading = false,
  isClearable,
  isSearchable = true,
  onBlur,
  helperText,
  className = "",
  loadOptions,
  defaultOptions = true,
  loadingMessage = () => <span className="text-sm">Loading...</span>,
  cacheOptions = true,
  formatOptionLabel,
  controlHeight,
  wrapMultiValues = false,
  mutedValue = false,
  noOptionsMessage = isCreatable
    ? ({ inputValue }) =>
        inputValue
          ? `Press Enter to add "${inputValue}"`
          : "Type values or select from list"
    : undefined,
}) => {
  const SelectComponent = loadOptions
    ? AsyncSelect
    : isCreatable
      ? CreatableSelect
      : Select;

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label htmlFor={inputId} className="admin-label">
          {label}
          {required && <span className="admin-required">*</span>}
        </label>
      )}

      <div className="relative text-sm min-w-0">
        <SelectComponent
          styles={customStyles(
            error,
            controlHeight,
            mutedValue,
            wrapMultiValues,
            isMulti,
          )}
          className="capitalize"
          classNamePrefix="admin-filter-select"
          inputId={inputId}
          name={name}
          {...(loadOptions
            ? {
                loadOptions,
                defaultOptions,
                cacheOptions,
              }
            : {
                options,
              })}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isDisabled={isDisabled}
          placeholder={placeholder}
          isMulti={isMulti}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          formatOptionLabel={formatOptionLabel}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          aria-invalid={Boolean(error)}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          components={{
            DropdownIndicator: CustomDropdownIndicator,
            LoadingIndicator: CustomLoadingIndicator,
          }}
        />

        {error ? (
          <p className="admin-field-error" role="alert">
            {error}
          </p>
        ) : (
          helperText && <p className="admin-field-help">{helperText}</p>
        )}
      </div>
    </div>
  );
};

export default FilterSelect;
